import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { SelectChangeEvent, useMediaQuery, useTheme } from '@mui/material';
import { User, useGetUsers, useUpdateUser } from '../../hooks/api/user';
import { useGetPlans } from '../../hooks/api/plan';
import { useNavigate } from 'react-router-dom';
import './students.scss';
import InstructorStudentsDesktopView from './InstructorStudentsDesktopView';
import MobileStudentsList from './MobileStudentsList';

const DEFAULT_ROWS_PER_PAGE = 50;

const getStudentsStats = (students: User[]) => {
  const activeStudents = students.filter((user) => user.plan).length;
  const studentsWithoutPlan = students.length - activeStudents;
  const averageClasses = students.length
    ? Math.round(students.reduce((sum, user) => sum + user.planBookings, 0) / students.length)
    : 0;

  return [
    {
      label: 'Total visibles',
      value: students.length,
      helper: 'Alumnos en la tabla',
    },
    {
      label: 'Con plan activo',
      value: activeStudents,
      helper: `${studentsWithoutPlan} sin plan`,
    },
    {
      label: 'Media de clases',
      value: averageClasses,
      helper: 'Clases registradas por alumno',
    },
  ];
};

export default function StudentsPage() {
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const getUsersQuery = useGetUsers({ hasPlan: showActiveOnly });
  const users = useMemo(() => getUsersQuery.data ?? [], [getUsersQuery.data]);
  const plans = useGetPlans().data ?? [];
  const updateUserMutation = useUpdateUser();
  const [searchText, setSearchText] = useState('');
  const deferredSearchText = useDeferredValue(searchText);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const students = useMemo(
    () =>
      users.filter(
        (user) =>
          !user.instructor &&
          (user.nickName?.toLowerCase().includes(deferredSearchText) ||
            user.firstName?.toLowerCase().includes(deferredSearchText) ||
            user.lastName?.toLowerCase().includes(deferredSearchText)) &&
          (!showActiveOnly || user.plan),
      ),
    [users, deferredSearchText, showActiveOnly],
  );
  const paginatedStudents = useMemo(() => {
    const start = page * rowsPerPage;
    return students.slice(start, start + rowsPerPage);
  }, [students, page, rowsPerPage]);
  const stats = useMemo(() => getStudentsStats(students), [students]);

  const handlePlanChange = (event: SelectChangeEvent, userId: number) => {
    const value = event.target.value as string;
    const planId = value === 'no plan' ? null : value;
    updateUserMutation.mutate({
      id: userId,
      changeset: { planId: planId, paidAt: planId ? new Date().toISOString() : null },
    });
  };

  const navigateToStudent = (userId: number) => {
    navigate(`/students/${userId}`);
  };

  const handleRowsPerPageChange = (nextRowsPerPage: number) => {
    setRowsPerPage(nextRowsPerPage);
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [deferredSearchText, showActiveOnly]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(students.length / rowsPerPage) - 1);

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, rowsPerPage, students.length]);

  if (isDesktop) {
    return (
      <InstructorStudentsDesktopView
        students={paginatedStudents}
        totalStudents={students.length}
        stats={stats}
        plans={plans}
        searchText={searchText}
        showActiveOnly={showActiveOnly}
        page={page}
        rowsPerPage={rowsPerPage}
        onSearchChange={(nextSearchText) => setSearchText(nextSearchText.toLowerCase())}
        onToggleActiveOnly={() => setShowActiveOnly(!showActiveOnly)}
        onStudentClick={navigateToStudent}
        onPlanChange={handlePlanChange}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    );
  }

  return (
    <MobileStudentsList
      students={paginatedStudents}
      totalStudents={students.length}
      plans={plans}
      onStudentClick={navigateToStudent}
      onPlanChange={handlePlanChange}
      onSearchChange={(nextSearchText) => setSearchText(nextSearchText.toLowerCase())}
      onToggleActiveOnly={() => setShowActiveOnly(!showActiveOnly)}
      showActiveOnly={showActiveOnly}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={setPage}
    />
  );
}
